<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <i class="fas fa-headset"></i> Support Tickets (Admin)
                        </h4>
                    </div>
                </div>
            </div>

            <!-- Stats cards -->
            <div class="row mb-3">
                <div class="col-md-3"><div class="card p-3 text-center"><small class="text-muted">Open</small><h3 class="mb-0 text-warning">{{ stats.open }}</h3></div></div>
                <div class="col-md-3"><div class="card p-3 text-center"><small class="text-muted">Diproses</small><h3 class="mb-0 text-info">{{ stats.in_progress }}</h3></div></div>
                <div class="col-md-3"><div class="card p-3 text-center"><small class="text-muted">Menunggu User</small><h3 class="mb-0 text-secondary">{{ stats.waiting_user }}</h3></div></div>
                <div class="col-md-3"><div class="card p-3 text-center"><small class="text-muted">Closed Hari Ini</small><h3 class="mb-0 text-success">{{ stats.closed_today }}</h3></div></div>
            </div>

            <!-- Filter -->
            <div class="card p-3 mb-3">
                <div class="row align-items-center">
                    <div class="col-md-3">
                        <select v-model="filter.status" @change="loadList" class="form-control form-control-sm">
                            <option value="">Semua Status</option>
                            <option value="unread">🔴 Belum Dibaca</option>
                            <option value="open">Open</option>
                            <option value="in_progress">Diproses</option>
                            <option value="waiting_user">Menunggu User</option>
                            <option value="closed">Selesai</option>
                            <option value="refund">Refund</option>
                        </select>
                    </div>
                    <div class="col-md-7">
                        <input v-model="filter.search" @keyup.enter="loadList" class="form-control form-control-sm" placeholder="Cari ID, ticket number, atau subject...">
                    </div>
                    <div class="col-md-2">
                        <button @click="loadList" class="btn btn-primary btn-sm btn-block">
                            <i class="fas fa-search"></i> Cari
                        </button>
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div v-if="loading" class="text-center p-5"><div class="spinner-border"></div></div>
            <div v-else-if="tickets.length === 0" class="card p-5 text-center text-muted">
                <i class="fas fa-inbox fa-3x mb-3" style="color:#d1d5db;"></i>
                <h5>Tidak ada tiket</h5>
            </div>
            <div v-else class="card p-0">
                <div class="d-flex justify-content-between align-items-center mb-2 px-1" data-name="auto-refresh-on">
                <small class="text-muted">
                    <i class="fas fa-sync-alt" :class="{'fa-spin': loading}"></i>
                    Auto-refresh: <strong>{{ autoRefreshEnabled ? 'ON' : 'OFF' }}</strong>
                    <span v-if="autoRefreshEnabled">setiap {{ autoRefreshSeconds }}s</span>
                    · Update terakhir: <strong>{{ formatLastRefresh() }}</strong>
                </small>
                <div>
                    <button @click="toggleSound" class="btn btn-sm btn-light mr-1">
                        <i :class="soundEnabled ? 'fas fa-volume-up text-success' : 'fas fa-volume-mute text-muted'"></i>
                    </button>
                    <button @click="toggleAutoRefresh" class="btn btn-sm" :class="autoRefreshEnabled ? 'btn-success' : 'btn-secondary'">
                        <i :class="autoRefreshEnabled ? 'fas fa-pause' : 'fas fa-play'"></i>
                        {{ autoRefreshEnabled ? 'Pause' : 'Resume' }}
                    </button>
                    <button @click="loadList(false)" class="btn btn-sm btn-light" title="Refresh sekarang">
                        <i class="fas fa-sync-alt"></i>
                    </button>
                </div>
            </div>
            <table class="table table-hover mb-0">
                    <thead style="background:#f9fafb;">
                        <tr>
                            <th>Status</th>
                            <th>Tiket</th>
                            <th>User</th>
                            <th>Priority</th>
                            <th>Update</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="t in tickets" :key="t.id" :class="{ 'table-warning': t.admin_unread_count > 0 }">
                            <td><span class="status-badge" :class="'status-' + t.status">{{ statusLabel(t.status) }}</span></td>
                            <td>
                                <div><strong>{{ t.subject }}</strong>
                                    <span v-if="t.admin_unread_count > 0" class="badge badge-danger">{{ t.admin_unread_count }} baru</span>
                                </div>
                                <small class="text-muted">{{ t.ticket_number }} · {{ categoryLabel(t.category) }}</small>
                            </td>
                            <td>
                                <div>{{ t.user_name || '-' }}</div>
                                <small class="text-muted">{{ t.user_email }}</small>
                            </td>
                            <td><span class="priority-badge" :class="'priority-' + t.priority">{{ priorityLabel(t.priority) }}</span></td>
                            <td><small>{{ formatRelative(t.updated_at) }}</small></td>
                            <td>
                                <router-link :to="'/admin/support-tickets/' + t.id" class="btn btn-sm btn-primary">
                                    <i class="fas fa-eye"></i> Buka
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="total > tickets.length" class="text-center mt-3">
                <button @click="loadMore" class="btn btn-light">Load More ({{ tickets.length }} / {{ total }})</button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
export default {
    data() {
        return {
            loading: false,
            tickets: [],
            total: 0,
            page: 1,
            stats: { open: 0, in_progress: 0, waiting_user: 0, closed: 0, closed_today: 0, total_unread: 0 },
            filter: { status: '', search: '' },
            // auto-refresh state
            autoRefreshInterval: null,
            autoRefreshEnabled: true,
            autoRefreshSeconds: 15,
            lastRefreshAt: null,
            previousTotal: 0,
            previousLatestTicketId: 0,
            soundEnabled: true,
            tabVisible: true,
        };
    },
    mounted() {
        this.loadList();
        this.loadStats();
        this.startAutoRefresh();
        // request browser notification permission (silent)
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {});
        }
        // pause polling kalau tab tidak aktif (save battery)
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    beforeDestroy() {
        this.stopAutoRefresh();
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
    methods: {
        async loadList(append = false, silent = false) {
            if (!silent) this.loading = true;
            try {
                const params = { page: append ? this.page + 1 : 1, limit: 30 };
                if (this.filter.status) params.status = this.filter.status;
                if (this.filter.search) params.search = this.filter.search;
                const r = await this.$store.dispatch('admin_support_ticket_list', params);
                const newTickets = r.data.data.tickets || [];
                const newTotal = r.data.data.total || 0;
                if (append) {
                    this.tickets = [...this.tickets, ...newTickets];
                    this.page++;
                } else {
                    // detect new tickets (compare top ticket id)
                    if (silent && newTickets.length > 0 && this.previousLatestTicketId > 0) {
                        const newTopId = newTickets[0].id;
                        if (newTopId > this.previousLatestTicketId) {
                            const newCount = newTickets.filter(t => t.id > this.previousLatestTicketId).length;
                            this.notifyNewTickets(newCount, newTickets[0]);
                        }
                    }
                    this.tickets = newTickets;
                    this.page = 1;
                    if (newTickets.length > 0) {
                        this.previousLatestTicketId = newTickets[0].id;
                    }
                }
                this.previousTotal = this.total;
                this.total = newTotal;
                this.lastRefreshAt = new Date();
            } catch (e) {
                if (!silent) this.$toast.error('Gagal load list');
            }
            if (!silent) this.loading = false;
        },
        startAutoRefresh() {
            if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval);
            if (!this.autoRefreshEnabled) return;
            this.autoRefreshInterval = setInterval(() => {
                if (this.tabVisible && !this.loading) {
                    this.loadList(false, true); // silent reload
                    this.loadStats();
                }
            }, this.autoRefreshSeconds * 1000);
        },
        stopAutoRefresh() {
            if (this.autoRefreshInterval) {
                clearInterval(this.autoRefreshInterval);
                this.autoRefreshInterval = null;
            }
        },
        toggleAutoRefresh() {
            this.autoRefreshEnabled = !this.autoRefreshEnabled;
            if (this.autoRefreshEnabled) {
                this.startAutoRefresh();
                this.$toast.success('Auto-refresh AKTIF (setiap ' + this.autoRefreshSeconds + ' detik)');
            } else {
                this.stopAutoRefresh();
                this.$toast.info('Auto-refresh dimatikan');
            }
        },
        toggleSound() {
            this.soundEnabled = !this.soundEnabled;
            this.$toast.info('Sound notif: ' + (this.soundEnabled ? 'ON' : 'OFF'));
        },
        handleVisibilityChange() {
            this.tabVisible = !document.hidden;
        },
        notifyNewTickets(count, latestTicket) {
            const msg = count + ' tiket baru masuk!';
            this.$toast.success(msg + ' (' + (latestTicket.ticket_number || '') + ')');
            // browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
                try {
                    const n = new Notification('StarSender — Tiket Baru', {
                        body: count + ' tiket: ' + (latestTicket.subject || latestTicket.ticket_number || 'tiket support'),
                        icon: '/favicon.ico',
                        tag: 'starsender-support-' + latestTicket.id,
                    });
                    n.onclick = () => { window.focus(); n.close(); };
                } catch (e) { /* silent */ }
            }
            // sound notification
            if (this.soundEnabled) {
                this.playBeep();
            }
        },
        playBeep() {
            try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.frequency.value = 880;
                osc.type = 'sine';
                gain.gain.setValueAtTime(0.25, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
                osc.start();
                osc.stop(ctx.currentTime + 0.3);
            } catch (e) { /* silent */ }
        },
        formatLastRefresh() {
            if (!this.lastRefreshAt) return '-';
            const diff = Math.floor((Date.now() - this.lastRefreshAt.getTime()) / 1000);
            if (diff < 5) return 'baru saja';
            if (diff < 60) return diff + ' detik lalu';
            return Math.floor(diff / 60) + ' menit lalu';
        },
        async loadMore() { this.loadList(true); },
        async loadStats() {
            try {
                const r = await this.$store.dispatch('admin_support_ticket_stats');
                this.stats = r.data.data.stats || this.stats;
            } catch (e) { /* silent */ }
        },
        statusLabel(s) { return { open: 'Open', in_progress: 'Diproses', waiting_user: 'Menunggu', closed: 'Selesai', refund: 'Refund' }[s] || s; },
        categoryLabel(c) { return { general: 'Umum', bug: 'Bug', billing: 'Pembayaran', feature: 'Fitur', other: 'Lain' }[c] || c; },
        priorityLabel(p) { return { low: 'Rendah', normal: 'Normal', high: 'Tinggi', urgent: 'Urgent' }[p] || p; },
        formatRelative(ts) { return ts ? moment(ts).fromNow() : '-'; },
    },
};
</script>

<style scoped>
.status-badge, .priority-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
}
.status-open { background: #fef3c7; color: #92400e; }
.status-in_progress { background: #dbeafe; color: #1e40af; }
.status-waiting_user { background: #fce7f3; color: #9d174d; }
.status-closed { background: #d1fae5; color: #065f46; }
.status-refund { background: #ede9fe; color: #5b21b6; }
.priority-low { background: #e5e7eb; color: #374151; }
.priority-normal { background: #dbeafe; color: #1e40af; }
.priority-high { background: #fef3c7; color: #92400e; }
.priority-urgent { background: #fee2e2; color: #991b1b; }
</style>
